<template>
    <!-- Single-source: render one NuxtImg -->
    <NuxtImg v-if="state.kind === 'single'" v-bind="binds" :sizes="sizes" :loading="loading" :quality="quality" />

    <!-- Two sources (art direction): render mobile + desktop with CSS breakpoints -->
    <template v-else>
        <!-- Mobile image (visible on small screens) -->
        <NuxtImg v-bind="binds.mobile" :sizes="sizesMobile || sizes" :loading="loading" :quality="quality" />
        <!-- Desktop image (visible on md and above) -->
        <NuxtImg v-bind="binds.desktop" :sizes="sizesDesktop || sizes" :loading="loading" :quality="quality" />
    </template>
</template>

<script setup lang="ts">
/**
 * ResponsiveImage
 * - Accepts a desktop image (required) and an optional mobile image.
 * - If only desktop is provided => renders a single <NuxtImg>.
 * - If mobile is also provided => renders two <NuxtImg> elements and toggles them with CSS classes.
 * - Pass `sizes` to generate responsive srcset automatically (Nuxt Image feature).
 *   Docs: https://image.nuxt.com/usage/nuxt-img
 */

import type { APIMedia, APIMediaCollection } from "~~/shared/types/media";
import { twMerge } from "tailwind-merge";

type Input = string | APIMedia | APIMediaCollection | APIMediaCollection[] | null | undefined;

interface Props {
    /** Desktop image source (required) */
    desktop: Input;
    /** Mobile image source (optional) */
    mobile?: Input;

    /** NuxtImg optimization props */
    sizes?: string; // e.g., '100vw sm:50vw md:33vw'
    sizesMobile?: string; // optional override for the mobile image
    sizesDesktop?: string; // optional override for the desktop image
    loading?: "lazy" | "eager";
    format?: string; // e.g., 'webp'
    quality?: number; // e.g., 70
    class?: string; // shared classes

    /** Visibility classes (Tailwind by default) */
    mobileClass?: string; // defaults to "block md:hidden"
    desktopClass?: string; // defaults to "hidden md:block"
}

const props = withDefaults(defineProps<Props>(), {
    mobile: undefined,
    sizes: undefined,
    sizesMobile: undefined,
    sizesDesktop: undefined,
    quality: undefined,
    format: undefined,
    loading: "lazy",
    class: "",
    mobileClass: "block md:hidden",
    desktopClass: "hidden md:block",
});

const getImageOptions = computed(() => ({
    format: props.format,
    quality: props.quality,
    loading: props.loading,
}));

// Compute final rendering state from your helper (handles single vs. responsive)
const state = computed(() => getImage(props.desktop, props.mobile, getImageOptions.value));

const classes = computed(() => ({
    imgClass: twMerge(state.value.kind === "single" ? state.value.img.class : "", props.class),
    mobileClass: twMerge(state.value.kind === "responsive" ? state.value.mobile.class : "", props.class, props.mobileClass),
    desktopClass: twMerge(state.value.kind === "responsive" ? state.value.desktop.class : "", props.class, props.desktopClass),
}));

const binds = computed(() => {
    if (state.value.kind === "single") {
        return {
            ...state.value.img,
            class: classes.value.imgClass,
        };
    } else {
        return {
            mobile: {
                ...state.value.mobile,
                class: classes.value.mobileClass,
            },
            desktop: {
                ...state.value.desktop,
                class: classes.value.desktopClass,
            },
        };
    }
});
</script>
